/***
 *
 *   LAZY LOAD ERROR BOUNDARY
 *   Code-split routes (React.lazy) suspend navigations inside a
 *   React.startTransition. If a route chunk fails to load (stale
 *   chunk hash after a deploy, flaky network, blocked request),
 *   React Router has already changed the URL but React reverts to
 *   the last committed tree – the previous page stays on screen and
 *   every later navigation keeps failing because React.lazy caches
 *   the rejection. This boundary catches those chunk errors and
 *   recovers with a page reload so the browser picks up fresh assets.
 *
 **********/

import React from 'react';

const RELOAD_KEY = 'lazy_chunk_reload_at';
const RELOAD_WINDOW = 10000; // ms – prevents infinite reload loops

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || String(error);

  return (
    name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk [\w.-]+ failed/.test(message) ||
    /Failed to fetch dynamically imported module/.test(message)
  );
}

function reloadWithGuard() {
  const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || 0);

  if (Date.now() - lastReload > RELOAD_WINDOW) {
    sessionStorage.setItem(RELOAD_KEY, Date.now());
    window.location.reload();
  }
}

export class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    // Catch lazy import rejections that surface as unhandled rejections
    window.addEventListener('unhandledrejection', this.handleRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleRejection);
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) reloadWithGuard();
  }

  handleRejection = (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadWithGuard();
    }
  };

  render() {
    const { error } = this.state;

    if (error) {
      // Not a chunk error – propagate like there is no boundary at all
      if (!isChunkLoadError(error)) throw error;

      // Already reloaded recently and it failed again – offer a manual retry
      return (
        <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
          <p>A new version of the app is available.</p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(RELOAD_KEY);
              window.location.reload();
            }}
          >
            Refresh the page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
